/**
 * Vue2 图片上传通用工具 (Axios 版本)
 * 调用方法触发图片选择，上传后返回图片路径
 */
import axios from 'axios';
import baseURL from '@/utils/url'
// 默认上传接口地址（可根据项目实际配置修改）
const UPLOAD_API_URL = baseURL + '/api/upload';

// 创建唯一的文件输入框（避免重复创建）
let fileInput = null;

/**
 * 初始化文件输入框
 */
function initFileInput() {
    if (fileInput) return fileInput;

    // 创建隐藏的file输入框
    fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*'; // 只允许选择图片
    fileInput.style.display = 'none';

    // 添加到body
    document.body.appendChild(fileInput);

    return fileInput;
}

/**
 * 上传图片核心方法
 * @param {Object} options - 可选配置
 * @param {string} options.accept - 允许的文件类型，默认 'image/*'
 * @param {number} options.maxSize - 最大文件大小(MB)，默认 5
 * @param {string} options.apiUrl - 自定义上传接口地址
 * @param {Object} options.axiosConfig - 自定义axios配置（如headers、timeout等）
 * @returns {Promise<string>} 返回图片路径
 */
export function uploadImage(options = {}) {
    return new Promise((resolve, reject) => {
        // 合并默认配置
        const config = {
            accept: 'image/*',
            maxSize: 5,
            apiUrl: UPLOAD_API_URL,
            axiosConfig: {}, // 自定义axios配置
            ...options
        };

        // 初始化文件输入框
        const input = initFileInput();
        input.accept = config.accept;

        // 移除之前的事件监听（避免重复触发）
        input.removeEventListener('change', handleFileChange);

        // 绑定文件选择事件
        input.addEventListener('change', handleFileChange);

        // 触发文件选择弹窗
        input.click();

        /**
         * 处理文件选择事件
         * @param {Event} e 
         */
        async function handleFileChange(e) {
            const files = e.target.files;

            // 未选择文件
            if (!files || files.length === 0) {
                reject(new Error('未选择图片文件'));
                return;
            }

            const file = files[0];

            // 验证文件大小
            const maxSizeBytes = config.maxSize * 1024 * 1024;
            if (file.size > maxSizeBytes) {
                reject(new Error(`图片大小不能超过 ${config.maxSize}MB`));
                // 清空input值，否则同一张图片无法重复选择
                input.value = '';
                return;
            }

            try {
                // 创建FormData
                const formData = new FormData();
                formData.append('file', file); // 注意：key要和后端接口要求一致

                // 调用上传接口（axios版本）
                const imageUrl = await uploadFile(formData, config);
                resolve(imageUrl);
            } catch (error) {
                reject(error);
            } finally {
                // 清空input值
                input.value = '';
            }
        }
    });
}

/**
 * 调用上传接口 (Axios 实现)
 * @param {FormData} formData 
 * @param {Object} config - 配置项
 * @returns {Promise<string>}
 */
async function uploadFile(formData, config) {
    try {
        // 发送axios请求
        const response = await axios({
            method: 'post',
            url: config.apiUrl,
            data: formData,
            // 合并自定义axios配置（如headers、timeout等）
            ...config.axiosConfig,
            // 强制设置Content-Type为multipart/form-data（FormData会自动处理边界符）
            headers: {
                'Content-Type': 'multipart/form-data',
                ...config.axiosConfig.headers
            }
        });

        // 解析接口返回结果（根据你的实际接口返回格式修改）
        // 示例接口返回格式：{ code: 200, data: { url: 'https://xxx.jpg' }, msg: '成功' }
        const res = response.data;
        console.log('res',res);
        
        if (res.code === 200 ) {
            return res.url; // 返回图片路径
        } else {
            throw new Error(res.msg || '上传失败，接口返回异常');
        }
    } catch (error) {
        // 统一处理axios错误
        if (error.response) {
            // 请求已发送，服务器返回非2xx状态码
            const errMsg = error.response.data?.msg || `上传失败，HTTP状态码：${error.response.status}`;
            throw new Error(errMsg);
        } else if (error.request) {
            // 请求已发送，但未收到响应（网络错误）
            throw new Error('网络错误，无法连接服务器');
        } else {
            // 请求配置错误
            throw new Error(`上传失败：${error.message}`);
        }
    }
}

// 可选：提供一个批量上传的方法（扩展功能）
export async function uploadImages(options = {}) {
    // 允许选择多张图片
    options.accept = options.accept || 'image/*';
    const multiple = options.multiple || false;

    return new Promise((resolve, reject) => {
        const input = initFileInput();
        input.accept = options.accept;
        input.multiple = multiple; // 开启多文件选择

        input.removeEventListener('change', handleMultiFileChange);
        input.addEventListener('change', handleMultiFileChange);
        input.click();

        async function handleMultiFileChange(e) {
            const files = e.target.files;
            if (!files || files.length === 0) {
                reject(new Error('未选择图片文件'));
                return;
            }

            try {
                // 批量上传
                const uploadPromises = Array.from(files).map(file => {
                    // 验证单文件大小
                    const maxSizeBytes = (options.maxSize || 5) * 1024 * 1024;
                    if (file.size > maxSizeBytes) {
                        throw new Error(`文件 ${file.name} 大小超过 ${options.maxSize || 5}MB`);
                    }
                    const formData = new FormData();
                    formData.append('file', file);
                    return uploadFile(formData, options);
                });

                // 等待所有上传完成
                const imageUrls = await Promise.all(uploadPromises);
                resolve(imageUrls);
            } catch (error) {
                reject(error);
            } finally {
                input.value = '';
                input.multiple = false; // 恢复默认
            }
        }
    });
}


 // 单张图片上传
    // async handleSingleUpload() {
    //   try {
    //     // 调用上传方法，可传入自定义配置
    //     const imagePath = await uploadImage({
    //       maxSize: 2, // 限制最大2MB
    //       apiUrl: '/api/custom/upload', // 自定义接口地址
    //       // 自定义axios配置（如添加token）
    //       axiosConfig: {
    //         headers: {
    //           'Authorization': 'Bearer ' + localStorage.getItem('token')
    //         },
    //         timeout: 30000 // 超时时间30秒
    //       }
    //     });
        
    //     this.imageUrl = imagePath;
    //     alert('上传成功！');
    //     console.log('图片路径：', imagePath);
    //   } catch (error) {
    //     // 友好提示错误信息
    //     alert(error.message);
    //     console.error('上传失败：', error);
    //   }
    // },

    // // 多张图片上传
    // async handleMultiUpload() {
    //   try {
    //     const paths = await uploadImages({
    //       maxSize: 5,
    //       multiple: true // 允许选择多张
    //     });
        
    //     this.imageUrls = paths;
    //     alert(`成功上传 ${paths.length} 张图片！`);
    //   } catch (error) {
    //     alert(error.message);
    //     console.error('批量上传失败：', error);
    //   }
    // }